/**
 * The todo form's edit rules for repeats (spec 2026-10-02 §5.3): what changed,
 * which "apply to" choices the save/delete step offers, and the request each
 * choice sends. Pure — the form and the data hook share it.
 */
import type {
  CreateResidentTodoInput,
  EditScope,
  RepeatRule,
  ResidentTodo,
  TodoFormValues,
  UpdateResidentTodoInput,
} from '@/types/residents/todos';
import { formatDayHeading, parseDateKey } from './todoCalendar';
import { describeRepeat, firstDate, followDate, sameRule } from './todoRecurrence';

export interface FormChanges {
  title: boolean;
  notes: boolean;
  date: boolean;
  /** The time of day — removing it included. Like title / notes, it adds or removes no choice. */
  time: boolean;
  /** The rule or its end date — turning the repeat off included. */
  repeat: boolean;
  /** A repeat switched to Never. */
  toNever: boolean;
  any: boolean;
}

export interface ScopeChoice {
  scope: EditScope;
  label: string;
  caption: string;
}

const LABELS: Record<EditScope, string> = {
  single: 'Only this date',
  following: 'This and following',
  all: 'All dates',
};

/** The values the form opens with: the item in edit mode, an empty todo on `initialDateKey` in create mode. */
export function valuesFromTodo(todo: ResidentTodo | null, initialDateKey: string): TodoFormValues {
  return {
    title: todo?.title ?? '',
    dueDate: todo?.dueDate ?? initialDateKey,
    dueTime: todo?.dueTime ?? null,
    notes: todo?.notes,
    repeat: todo?.repeat ?? null,
  };
}

const sameText = (a: string | undefined, b: string | undefined) =>
  (a ?? '').trim() === (b ?? '').trim();

export function diffValues(initial: TodoFormValues, values: TodoFormValues): FormChanges {
  const title = !sameText(initial.title, values.title);
  const notes = !sameText(initial.notes, values.notes);
  const date = initial.dueDate !== values.dueDate;
  const time = (initial.dueTime ?? null) !== (values.dueTime ?? null);
  const before = initial.repeat ?? null;
  const after = values.repeat ?? null;
  const repeat = !sameRule(before, after);
  const toNever = repeat && before !== null && after === null;
  return { title, notes, date, time, repeat, toNever, any: title || notes || date || time || repeat };
}

/** On a repeat's first date "This and following" covers every date, so it shows as "All dates". */
const foldFirstDate = (scopes: EditScope[], isFirstDate: boolean): EditScope[] =>
  isFirstDate
    ? scopes
        .map(scope => (scope === 'following' ? 'all' : scope))
        .filter((scope, index, all) => all.indexOf(scope) === index)
    : scopes;

/** The save step's choices — the spec §5.3 table. */
export function saveScopes(changes: FormChanges, isFirstDate: boolean): EditScope[] {
  let scopes: EditScope[];
  if (changes.toNever) scopes = ['following', 'all'];
  else if (changes.repeat && changes.date) scopes = ['following'];
  else if (changes.repeat) scopes = ['following', 'all'];
  else if (changes.date) scopes = ['single', 'following'];
  else scopes = ['single', 'following', 'all'];
  return foldFirstDate(scopes, isFirstDate);
}

export const deleteScopes = (isFirstDate: boolean): EditScope[] =>
  foldFirstDate(['single', 'following', 'all'], isFirstDate);

const short = (key: string): string => formatDayHeading(parseDateKey(key));
const lowerFirst = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

/**
 * The rule a (re)started repeat runs on. Nothing follows unless the date moved; then every part
 * equal to the OLD date's follows it (spec §5.3: a single weekday equal to the old weekday, a day
 * of month equal to the old day) and every other part stays. `oldKey` is the opened item's
 * SCHEDULED date — never a moved (shown) one: the rule's weekday / day came from the scheduled date.
 */
function restartRule(oldKey: string, values: TodoFormValues, changes: FormChanges): RepeatRule | null {
  const rule = values.repeat ?? null;
  if (!rule || !changes.date) return rule;
  return followDate(rule, oldKey, values.dueDate);
}

/**
 * The date a restart choice starts its repeat on: the new date when the date changed; else the
 * opened item's SCHEDULED date for "This and following" (the planner starts the new repeat there,
 * not on the day a moved date shows on) and the series start for "All dates" (undefined when the
 * item does not carry it).
 */
function restartStart(
  scope: Exclude<EditScope, 'single'>,
  todo: ResidentTodo,
  values: TodoFormValues,
  changes: FormChanges,
): string | undefined {
  if (changes.date) return values.dueDate;
  return scope === 'following' ? todo.occurrenceDate : todo.seriesStart;
}

/**
 * Whether a restart choice would leave the repeat with no dates at all, which the planner refuses
 * (422). A start the item does not carry cannot be checked, so the choice stays.
 */
function restartsWithNoDates(
  scope: EditScope,
  todo: ResidentTodo,
  values: TodoFormValues,
  changes: FormChanges,
  rule: RepeatRule | null,
): boolean {
  if (scope === 'single' || !rule) return false;
  const start = restartStart(scope, todo, values, changes);
  return start !== undefined && firstDate(rule, start) === null;
}

function saveCaption(
  scope: EditScope,
  todo: ResidentTodo,
  values: TodoFormValues,
  changes: FormChanges,
  rule: RepeatRule | null,
): string {
  const shown = short(todo.dueDate);
  if (scope === 'single') {
    return changes.date ? `Moves ${shown} to ${short(values.dueDate)}` : `Changes ${shown} only`;
  }
  if (changes.toNever) {
    // The one-time todo lands on the chosen date (the opened one when the date did not change).
    return scope === 'following'
      ? `${short(values.dueDate)} becomes a one-time todo; later dates are removed`
      : `Keeps only ${short(values.dueDate)}, as a one-time todo`;
  }
  // Where the restarted repeat starts: the captions name it, and a yearly rule takes its month from it.
  const startsOn = restartStart(scope, todo, values, changes) ?? values.dueDate;
  const describe = (r: RepeatRule): string => {
    const phrase = lowerFirst(describeRepeat(r, startsOn));
    return r.until ? `${phrase}, until ${short(r.until)}` : phrase;
  };
  if (scope === 'following') {
    return (changes.date || changes.repeat) && rule
      ? `From ${short(startsOn)}: ${describe(rule)}`
      : `${shown} and every date after it`;
  }
  if (changes.date && rule) return `The repeat starts ${short(startsOn)}: ${describe(rule)}`;
  if (changes.repeat && rule) return `Every date: ${describe(rule)}`;
  return 'Every date, past ones too';
}

/**
 * The save step's choices, each with a caption that says what it will do.
 *
 * A restart choice ("This and following" / "All dates" with a changed repeat or date) starts its
 * repeat on the new date, else — "This and following" — on the opened date's SCHEDULED date (never
 * the day a moved date shows on) and — "All dates" — on the series start (`todo.seriesStart`). Its
 * caption describes the repeat from there, and the choice is DROPPED when the rule it would send
 * has no dates from there — the planner would refuse it (422); an unknown start is kept.
 * "Only this date" and choices with no restart rule (Repeat → Never) are never dropped. When a
 * change needs a scope and none is left, the form shows "Ends must be after the first date".
 */
export function saveChoices(
  todo: ResidentTodo,
  initial: TodoFormValues,
  values: TodoFormValues,
): ScopeChoice[] {
  const changes = diffValues(initial, values);
  const rule = restartRule(todo.occurrenceDate ?? initial.dueDate, values, changes);
  return saveScopes(changes, todo.isFirstDate ?? false)
    .filter(scope => !restartsWithNoDates(scope, todo, values, changes, rule))
    .map(scope => ({
      scope,
      label: LABELS[scope],
      caption: saveCaption(scope, todo, values, changes, rule),
    }));
}

/**
 * The scope Save sends without the step, or null when the step asks. A question with one answer is
 * not asked: the one choice left is sent. Repeat → Never still asks, its one choice (on the first
 * date) the confirmation: it removes every other date, as a delete would.
 */
export function directSaveScope(
  todo: ResidentTodo,
  initial: TodoFormValues,
  values: TodoFormValues,
): EditScope | null {
  if (diffValues(initial, values).toNever) return null;
  const choices = saveChoices(todo, initial, values);
  return choices.length === 1 ? choices[0].scope : null;
}

export function deleteChoices(todo: ResidentTodo): ScopeChoice[] {
  const shown = short(todo.dueDate);
  const captions: Record<EditScope, string> = {
    single: `Removes ${shown}`,
    following: `Removes ${shown} and every date after it`,
    all: 'Deletes the todo and every date',
  };
  return deleteScopes(todo.isFirstDate ?? false).map(scope => ({
    scope,
    label: LABELS[scope],
    caption: captions[scope],
  }));
}

/** POST body: absent notes, no time and a null repeat are left out. */
export function buildCreateInput(values: TodoFormValues): CreateResidentTodoInput {
  return {
    title: values.title,
    dueDate: values.dueDate,
    ...(values.dueTime ? { dueTime: values.dueTime } : {}),
    ...(values.notes ? { notes: values.notes } : {}),
    ...(values.repeat ? { repeat: values.repeat } : {}),
  };
}

/**
 * The PATCH for a form save (spec 2026-10-02 §6.2; times: spec 2026-10-06 §7.5). A one-off sends
 * title, date and notes every time (notes null clears; a rule converts it) and its time only when
 * it changed (null removes it) — so a backend that predates times, whose strict schema refuses the
 * key, still takes every save that leaves the time alone. A repeating date sends its scope, its
 * SCHEDULED date and only what changed; a restart whose rule followed the date (from that scheduled
 * date) sends that rule too.
 */
export function buildUpdateInput(
  todo: ResidentTodo,
  values: TodoFormValues,
  scope?: EditScope,
): UpdateResidentTodoInput {
  if (!todo.seriesId || !todo.occurrenceDate) {
    const timeChanged = (todo.dueTime ?? null) !== (values.dueTime ?? null);
    return {
      id: todo.id,
      title: values.title,
      dueDate: values.dueDate,
      notes: values.notes ?? null,
      ...(timeChanged ? { dueTime: values.dueTime ?? null } : {}),
      ...(values.repeat ? { repeat: values.repeat } : {}),
    };
  }
  const initial = valuesFromTodo(todo, todo.dueDate);
  const changes = diffValues(initial, values);
  const input: UpdateResidentTodoInput = {
    id: todo.seriesId,
    scope: scope ?? 'all',
    occurrenceDate: todo.occurrenceDate,
  };
  if (changes.title) input.title = values.title;
  if (changes.notes) input.notes = values.notes ?? null;
  if (changes.date) input.dueDate = values.dueDate;
  if (changes.time) input.dueTime = values.dueTime ?? null;
  if (scope !== 'single') {
    const restart = restartRule(todo.occurrenceDate, values, changes);
    if (changes.repeat) input.repeat = restart;
    else if (restart && !sameRule(restart, initial.repeat ?? null)) input.repeat = restart;
  }
  return input;
}
